import { Badge, Button, Card, CardContent, Spinner } from '@fc/ui'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, CheckCircle2, MinusCircle, XCircle } from 'lucide-react'
import { useNavigate, useParams } from 'react-router-dom'
import { PageHeader } from '../../components/page-header'
import { getWorkflow, listWorkflowRuns, type WorkflowRunDto } from '../../lib/workflows-api'

const statusBadge: Record<string, 'success' | 'danger' | 'neutral'> = {
  succeeded: 'success',
  failed: 'danger',
  skipped: 'neutral',
}

function StatusIcon({ status }: { status: string }) {
  if (status === 'failed') return <XCircle className="size-4 text-danger" />
  if (status === 'skipped') return <MinusCircle className="size-4 text-dim" />
  return <CheckCircle2 className="size-4 text-success" />
}

export function WorkflowRuns() {
  const { id = '' } = useParams()
  const navigate = useNavigate()

  const { data: workflow } = useQuery({
    queryKey: ['workflows', id],
    queryFn: () => getWorkflow(id),
    enabled: Boolean(id),
  })
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['workflows', id, 'runs'],
    queryFn: () => listWorkflowRuns(id),
    enabled: Boolean(id),
  })

  return (
    <div className="space-y-6">
      <div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/app/workflows/${id}`)}
          className="mb-3"
        >
          <ArrowLeft />
          Back to workflow
        </Button>
        <PageHeader
          title={workflow ? `${workflow.name} — runs` : 'Run history'}
          description="An immutable, append-only log of every execution, including dry-runs."
        />
      </div>

      <Card className="overflow-hidden p-0">
        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-20 text-muted">
            <Spinner /> Loading runs…
          </div>
        ) : isError ? (
          <CardContent className="py-16 text-center">
            <p className="text-sm text-danger">Couldn’t load run history.</p>
            <p className="mt-1 text-xs text-dim">
              {error instanceof Error ? error.message : 'Please try again.'}
            </p>
          </CardContent>
        ) : !data || data.length === 0 ? (
          <CardContent className="py-16 text-center text-sm text-muted">
            No runs yet. Trigger the workflow or use Test to see it here.
          </CardContent>
        ) : (
          <ul className="divide-y divide-border-soft">
            {data.map((run: WorkflowRunDto) => (
              <li key={run.id} className="px-5 py-4">
                <div className="flex items-center gap-3">
                  <StatusIcon status={run.status} />
                  <Badge variant={statusBadge[run.status] ?? 'neutral'} className="capitalize">
                    {run.status}
                  </Badge>
                  {run.dryRun && (
                    <Badge variant="secondary" className="uppercase">
                      Dry-run
                    </Badge>
                  )}
                  <span className="text-sm text-muted">
                    {new Date(run.startedAt).toLocaleString()}
                  </span>
                </div>
                {run.error && <p className="mt-2 text-xs text-danger">{run.error}</p>}
                {run.steps.length > 0 && (
                  <ul className="mt-2 space-y-1 pl-7">
                    {run.steps.map((s, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs text-muted">
                        <span
                          className={
                            s.status === 'error'
                              ? 'text-danger'
                              : s.status === 'skipped'
                                ? 'text-dim'
                                : 'text-success'
                          }
                        >
                          {s.status === 'error' ? '✕' : s.status === 'skipped' ? '–' : '✓'}
                        </span>
                        <span>{s.detail}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  )
}
